import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';
import useEventBus from './useEventBus';
import { LOGGING_ATTR_PREFIX } from '../constants';

const UDS_LEAD_FORM_SUBMITTED = 'udsLeadFormSubmitted';

/**
 * Custom hook to manage UDS Lead Gate display logic.
 *
 * When UDS has Lead Gate enabled (from DR Admin config), payment information
 * is intentionally hidden until a shopper submits a lead form.
 *
 * Logic:
 * - If leadGatePaymentOnDS is false → not active (normal payment display)
 * - If leadGatePaymentOnDS is true AND localStorage has dealXgData → not active (lead already submitted)
 * - If leadGatePaymentOnDS is true AND sessionStorage has udsLeadFormSubmitted → not active
 * - If leadGatePaymentOnDS is true AND neither entry exists → active (hide payments/CTAs/incentives, show overlay)
 * - Listens to uds:submitLeadForm:completed event; if no error → deactivate and persist to sessionStorage
 *
 * @returns {boolean} isLeadGateActive - Whether the lead gate overlay should be shown
 */
const useUdsLeadGate = () => {
	const flags = useFlags();
	const isLeadGateFeatureFlag = flags['ws-track-my-offer-lead-gate'] || false;

	const leadGatePaymentOnDS = useSelector(
		(state) => state.udsLeadGate?.leadGatePaymentOnDS ?? false
	);
	const [eventBus, isReady] = useEventBus();

	const hasAlreadySubmitted = () => {
		try {
			// Also check sessionStorage for udsLeadFormSubmitted (set by this hook)
			return sessionStorage.getItem(UDS_LEAD_FORM_SUBMITTED) === 'true';
		} catch {
			return false;
		}
	};

	const [isLeadGateActive, setIsLeadGateActive] = useState(false);

	// Re-evaluate when redux state changes (e.g. after server render hydration)
	useEffect(() => {
		const dealXgDataStr = localStorage.getItem('dealXgData');
		const dealXgData = dealXgDataStr ? JSON.parse(dealXgDataStr) : null;
		const isExistDealXgId = !!dealXgData?.dealXgId;
		if (isExistDealXgId) {
			setIsLeadGateActive(false);
		} else {
			setIsLeadGateActive(isLeadGateFeatureFlag && leadGatePaymentOnDS && !hasAlreadySubmitted());
		}
	}, [isLeadGateFeatureFlag, leadGatePaymentOnDS]);

	useEffect(() => {
		if (!isReady || !isLeadGateFeatureFlag || !leadGatePaymentOnDS) return undefined;

		const handleLeadFormCompleted = (payload) => {
			if (payload.error) {
				logger.info(
					`${LOGGING_ATTR_PREFIX}: uds:submitLeadForm:completed failed with error`,
					{ hasError: !!payload?.error }
				);
			}
			else {
				logger.info(
					`${LOGGING_ATTR_PREFIX}: uds:submitLeadForm:completed received`,
					{ data: payload }
				);
				setIsLeadGateActive(false);
			}
		};

		eventBus.on('uds:submitLeadForm:completed', handleLeadFormCompleted);

		return () => {
			eventBus.off('uds:submitLeadForm:completed', handleLeadFormCompleted);
		};
	}, [isReady, eventBus, isLeadGateFeatureFlag, leadGatePaymentOnDS]);

	return isLeadGateActive;
};

export default useUdsLeadGate;
